export const DeletionTargetType = {
  USER: "USER",
  GUEST: "GUEST",
  HOTEL: "HOTEL",
  TENANT: "TENANT",
} as const;

export type DeletionTargetType =
  (typeof DeletionTargetType)[keyof typeof DeletionTargetType];

export const DeletionJobStatus = {
  PENDING: "PENDING",
  RUNNING: "RUNNING",
  COMPLETED: "COMPLETED",
  FAILED: "FAILED",
  CANCELLED: "CANCELLED",
} as const;

export type DeletionJobStatus =
  (typeof DeletionJobStatus)[keyof typeof DeletionJobStatus];

export const DeletionMode = {
  /** Irreversibly hard-delete everything (default; the only mode for non-guests). */
  PURGE: "PURGE",
  /** Guest only — scrub PII but keep bookings/transactions/billing for analytics. */
  ANONYMIZE: "ANONYMIZE",
} as const;

export type DeletionMode = (typeof DeletionMode)[keyof typeof DeletionMode];

const TERMINAL_DELETION_STATUSES = new Set<DeletionJobStatus>([
  DeletionJobStatus.COMPLETED,
  DeletionJobStatus.FAILED,
  DeletionJobStatus.CANCELLED,
]);

export const isTerminalDeletionStatus = (
  status: DeletionJobStatus | null | undefined,
): boolean => (status ? TERMINAL_DELETION_STATUSES.has(status) : false);

export interface DeletionProgressStep {
  step: string;
  status: "done" | "failed";
  count?: number;
  error?: string;
}

export interface DeletionJobSummary {
  counts: Record<string, number>;
  warnings: string[];
}

/** Mirrors the backend `DeletionJobResponseDto`. */
export interface DeletionJob {
  id: string;
  targetType: DeletionTargetType;
  targetId: string;
  targetLabel: string | null;
  tenantId: string;
  region: string | null;
  mode: DeletionMode;
  status: DeletionJobStatus;
  progress: DeletionProgressStep[] | null;
  summary: DeletionJobSummary | null;
  error: string | null;
  requestedById: string;
  requestedByEmail: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
}

/** Request body for every start-deletion endpoint. */
export interface StartDeletionDto {
  /** Exact confirmation token (tenant slug / hotel name / user email / guestId). */
  confirmation: string;
  /**
   * Required for HOTEL and SUPER_ADMIN GUEST targets; ignored for USER, TENANT,
   * and HOTEL_ADMIN guest calls (derived from the token).
   */
  tenantId?: string;
  /** GUEST only. Defaults to PURGE server-side; HOTEL_ADMIN must use ANONYMIZE. */
  mode?: DeletionMode;
}

export interface DeletionJobsQuery {
  status?: DeletionJobStatus;
  targetType?: DeletionTargetType;
  page?: number;
  limit?: number;
}

export interface PaginatedDeletionJobs {
  data: DeletionJob[];
  total: number;
  page: number;
  limit: number;
}

/** Which identifier the user must retype to confirm a wipe, per target type. */
export const CONFIRM_FIELD_BY_TARGET: Record<
  DeletionTargetType,
  "slug" | "name" | "email"
> = {
  TENANT: "slug",
  HOTEL: "name",
  USER: "email",
  GUEST: "email",
};

/** Everything a screen needs to drive the confirm → start → poll flow. */
export interface DeletionTargetDescriptor {
  type: DeletionTargetType;
  /** Path id (userId / guestId / hotelId / tenantId). */
  id: string;
  /** Human-readable identifier shown in the modal (slug / name / email). */
  label: string;
  /** The exact value the user must type to confirm. */
  expectedConfirmation: string;
  /** Required for HOTEL and SUPER_ADMIN GUEST; omit for HOTEL_ADMIN guest calls. */
  tenantId?: string;
  /** GUEST only — PURGE or ANONYMIZE. Drives the confirm copy and is sent in the body. */
  mode?: DeletionMode;
}
